import React, { useState, useEffect, useCallback } from "react";
import { api, useAuth } from "../Contexts/AuthContext";
import "../styles/Analytics.css";

const COLORS = {
  green:  "#799F0C",
  blue:   "#3b82f6",
  amber:  "#f59e0b",
  rose:   "#f43f5e",
  teal:   "#14b8a6",
  purple: "#8b5cf6",
};

function DonutChart({ data = [], centerText = "0%" }) {
  const total = data?.reduce((s, d) => s + (d.value || 0), 0) || 0;
  const r = 52;
  const circumference = 2 * Math.PI * r;
  let offset = 0;
  const segs = data.map((d) => {
    const val = d.value || 0;
    const dash = total > 0 ? (val / total) * circumference : 0;
    const seg = { ...d, dash, offset };
    offset += dash;
    return seg;
  });

  return (
    <div className="donut-area">
      <div className="donut-svg-wrap">
        <svg viewBox="0 0 130 130" width="130" height="130">
          {segs.map((s, i) => (
            <circle key={i} cx={65} cy={65} r={r} fill="none"
              stroke={s.color} strokeWidth="13"
              strokeDasharray={`${Math.max(0, s.dash - 2)} ${circumference}`}
              strokeDashoffset={-s.offset} strokeLinecap="round" />
          ))}
        </svg>
        <div className="donut-center">
          <div className="donut-center-val">{centerText}</div>
          <div className="donut-center-lbl">Ratio</div>
        </div>
      </div>
      <div className="donut-legend">
        {data.map((d, i) => (
          <div className="donut-leg-row" key={i}>
            <div className="donut-leg-left">
              <div className="donut-leg-swatch" style={{ background: d.color }} />
              {d.name}
            </div>
            <div className="donut-leg-val">{d.value}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("month");
  const [employee, setEmployee] = useState("All");
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateStart && dateEnd) {
        params.append("date_start", dateStart);
        params.append("date_end", dateEnd);
      } else {
        params.append("period", period);
      }
      params.append("employee", employee);

      const response = await api.get(`crm/analytics/?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  }, [period, employee, dateStart, dateEnd]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="an-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div className="loading-spinner">Loading Educational Analytics...</div>
      </div>
    );
  }

  if (!data) return null;

  const { kpis = {}, leadsByStatus = [], leadsBySource = [], callStats = {}, recentActivity = [], stats = [] } = data;

  return (
    <div className="an-main">
      <div className="an-topbar">
        <div className="an-page-meta">
          <div className="an-eyebrow">Institutional Insights</div>
          <h1 className="an-title">Analytics Dashboard</h1>
        </div>
        <div className="an-topbar-right">
          <button className="an-btn-primary" onClick={fetchAnalytics}>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="an-filter-bar">
        {data.employees && data.employees.length > 0 && (
          <div className="an-filter-item">
            <span className="an-filter-label">Filter by Employee</span>
            <select
              className="an-filter-select"
              value={employee}
              onChange={(e) => setEmployee(e.target.value)}
            >
              <option value="All">All Employees</option>
              {data.employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="an-filter-item">
          <span className="an-filter-label">Start Date</span>
          <input
            type="date"
            className="an-filter-date"
            value={dateStart}
            onChange={(e) => {
              setDateStart(e.target.value);
              setPeriod("custom");
            }}
          />
        </div>

        <div className="an-filter-item">
          <span className="an-filter-label">End Date</span>
          <input
            type="date"
            className="an-filter-date"
            value={dateEnd}
            onChange={(e) => {
              setDateEnd(e.target.value);
              setPeriod("custom");
            }}
          />
        </div>

        <div className="an-filter-item presets">
          <span className="an-filter-label">Preset Period</span>
          <div className="period-tabs">
            {[
              { id: "today", label: "Today" },
              { id: "week", label: "This Week" },
              { id: "month", label: "This Month" },
              { id: "all", label: "All Time" },
            ].map((p) => (
              <button
                key={p.id}
                className={`period-btn${period === p.id ? " active" : ""}`}
                onClick={() => {
                  setPeriod(p.id);
                  setDateStart("");
                  setDateEnd("");
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="stat-row">
        {stats.map((s, i) => (
          <div className="stat-cell" key={i}>
            <div className="stat-cell-val">{s.val}</div>
            <div className="stat-cell-lbl">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="kpi-grid">
        {[
          { label: "Total Leads", value: kpis.totalLeads, trend: "up", icon: "🎯", color: "blue", badge: kpis.leadsTrend },
          { label: "Converted Leads", value: kpis.convertedLeads, trend: "up", icon: "✅", color: "green", badge: kpis.conversionRate },
          { label: "Total Call Logs", value: kpis.totalCalls, trend: "flat", icon: "📞", color: "amber", badge: kpis.avgCallDuration },
        ].map((k, i) => (
          <div className={`kpi-card ${k.color}`} key={i}>
            <div className="kpi-top">
              <div className={`kpi-icon ${k.color}`}>{k.icon}</div>
              <span className={`kpi-badge ${k.trend}`}>{k.badge}</span>
            </div>
            <div className="kpi-value">{k.value}</div>
            <div className="kpi-label">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="grid-2-1">
        <div className="card">
          <div className="card-hd">
            <div className="card-hd-left">
              <div className="card-title">Lead Acquisition Sources</div>
              <div className="card-subtitle">Where your students come from</div>
            </div>
          </div>
          <DonutChart data={leadsBySource} centerText={kpis.totalLeads} />
        </div>

        <div className="card">
          <div className="card-hd">
            <div className="card-hd-left">
              <div className="card-title">Lead Pipeline Status</div>
              <div className="card-subtitle">Current distribution</div>
            </div>
          </div>
          <div className="device-list">
            {leadsByStatus.map((s, i) => (
              <div className="device-row" key={i} style={{ marginBottom: '15px' }}>
                <div className="device-top" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div className="device-name">{s.name}</div>
                  <div className="device-pct">{s.count}</div>
                </div>
                <div className="device-bar-bg" style={{ height: '8px', background: '#f1f5f9', borderRadius: '10px' }}>
                  <div className="device-fill" style={{ width: `${(s.count / kpis.totalLeads) * 100}%`, height: '100%', background: s.color, borderRadius: '10px' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-hd">
            <div className="card-hd-left">
              <div className="card-title">Telephony Analytics (Callyzer)</div>
              <div className="card-subtitle">Calls handled by your team</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div className="stat-card" style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Avg. Duration</div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--green)' }}>{callStats.avgDuration}</div>
            </div>
            <div className="stat-card" style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Duration</div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--blue)' }}>{callStats.totalDuration}</div>
            </div>
          </div>
          <div style={{ marginTop: '20px' }}>
             <table className="data-table">
                <thead>
                  <tr>
                    <th>Call Type</th>
                    <th>Count</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {callStats.breakdown?.map((item, i) => (
                    <tr key={i}>
                      <td>{item.type}</td>
                      <td>{item.count}</td>
                      <td>{item.pct}%</td>
                    </tr>
                  ))}
                </tbody>
             </table>
          </div>
        </div>

        <div className="card">
          <div className="card-hd">
            <div className="card-hd-left">
              <div className="card-title">Recent Activity Log</div>
              <div className="card-subtitle">Latest updates in CRM</div>
            </div>
            <span style={{ fontSize: 11, color: "var(--green)", fontWeight: 600 }}>● LIVE</span>
          </div>
          <div className="activity-list">
            {recentActivity.map((a, i) => (
              <div className="activity-item" key={i}>
                <div className="activity-icon" style={{ background: '#f2f7e4' }}>{a.icon}</div>
                <div className="activity-body">
                  <div className="activity-text" dangerouslySetInnerHTML={{ __html: a.text }} />
                  <div className="activity-time">{a.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
